import React from 'react'
import styled from 'styled-components'
import { transparentize } from 'polished'
import Highlight from 'react-highlighter'

import BaseImage, { BaseImageComponent } from 'components/Image'
import UnreadIndicator from 'components/UnreadIndicator'
import LoadingPlaceholder from 'components/LoadingPlaceholder'

const ArtboardImageWrapper = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 100%;
  margin-bottom: 20px;
  border-radius: 4px;
  overflow: hidden;
  background-color: ${({ theme }) =>
    transparentize(1 - 0.02, theme.colors.text.dark.base)};
  transition: background-color
    ${({ theme }) =>
      `${theme.transitions.duration[0]} ${theme.transitions.timing.easeInOut}`};
`

const ArtboardTitle = styled.h1`
  margin: 0;

  font-size: 0.8125rem;
  font-weight: ${({ theme }) => theme.fontWeights.regular};

  line-height: 1.25;
  color: ${({ theme }) => theme.colors.text.dark.primary};

  text-overflow: ellipsis;
  overflow: hidden;
`

const ArtboardTitleWrapper = styled.div`
  display: flex;

  justify-content: center;
  align-items: center;
`

const ArtboardWrapper = styled.section`
  text-align: center;

  :hover {
    ${ArtboardImageWrapper} {
      background-color: ${({ theme }) =>
        transparentize(1 - 0.06, theme.colors.text.dark.base)};
    }
  }
`

const ImageWrapper = styled.div`
  display: flex;

  justify-content: center;
  align-items: center;

  position: absolute;
  top: 0;
  left: 0;

  width: 100%;
  height: 100%;
`

const Image = styled(BaseImage)`
  display: flex;

  justify-content: center;
  align-items: center;

  width: 100%;
  height: 100%;
`

const BalloonUnreadIndicator = styled(UnreadIndicator)`
  font-size: 0.8125rem;
  font-weight: ${({ theme }) => theme.fontWeights.regular};

  line-height: 1.15; /* stylelint-disable-line scales/line-height */

  white-space: nowrap;
  margin-left: 8px;
`

const ImageComponent = styled(BaseImageComponent)`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  /* stylelint-disable font-family-no-missing-generic-family-keyword */
  font-family: 'object-fit: contain;';
`

interface ArtboardProps {
  className?: string
  title: string
  hightLightTitle?: string
  imageSrc?: string
  commentCount?: number
  hasNewComments?: boolean
  width?: number
  srcSet?: string
}

const Artboard: React.FC<ArtboardProps> = props => {
  const {
    className,
    title,
    imageSrc,
    commentCount,
    hasNewComments,
    hightLightTitle = '',
    width,
    srcSet,
  }: typeof props = props
  const hasComments = !!commentCount && commentCount > 0

  return (
    <ArtboardWrapper className={className}>
      <ArtboardImageWrapper>
        <ImageWrapper>
          <Image
            alt={`${title} artboard`}
            customImageElement={ImageComponent}
            src={imageSrc}
            width={width}
            srcSet={srcSet || undefined}
            loadingPlaceholder={<LoadingPlaceholder size="64px" />}
          />
        </ImageWrapper>
      </ArtboardImageWrapper>
      <ArtboardTitleWrapper>
        <ArtboardTitle>
          <Highlight search={hightLightTitle}>{title}</Highlight>
        </ArtboardTitle>
        {hasComments && (
          <BalloonUnreadIndicator
            data-testid="comment-text"
            amount={commentCount}
            highlight={hasNewComments}
          />
        )}
      </ArtboardTitleWrapper>
    </ArtboardWrapper>
  )
}

export default Artboard
